import { User, UserProfile, UserRole } from './auth';
import { EvaluatorAssignment } from './evaluator';
import { SectionBasedApplication } from './index';
// PaginationData's canonical definition lives in the UI library (src/shared);
// imported for local use and re-exported below for the rest of the app.
import type { PaginationData } from 'shared';

// Extended application statuses for admin use
export const AdminApplicationStatuses = [
  'draft',
  'submitted',
  'under_admissibility_review',
  'admissible',
  'under_evaluation',
  'evaluated',
  'selected',
  'not_selected',
  'not_admissible',
] as const;

export type AdminApplicationStatus = (typeof AdminApplicationStatuses)[number];

export interface ReviewerAssignment {
  adminId: string;
  adminName: string;
  assignedAt: string;
}

export interface StatusChange {
  status: AdminApplicationStatus;
  changedBy: string;
  changedByName: string;
  changedAt: string;
  notes?: string;
}

export interface ScoreStats {
  min: number;
  max: number;
  average: number;
  standardDeviation: number;
  count: number;
}

export interface EvaluationSummaryDto {
  criteriaDistribution: {
    originality_risk: ScoreStats;
    national_relevance: ScoreStats;
    global_scalability: ScoreStats;
    international_collaboration: ScoreStats;
    technical_viability: ScoreStats;
    results_quality: ScoreStats;
  };
  weightedScoreDistribution: ScoreStats;
  numberOfEvaluations: number;
  numberOfEvaluators: number;
  completionPercentage: number;
}

export interface EvaluationListItemDto {
  id: string;
  evaluatorName: string;
  updatedAt: string;
  status: string; // 'submitted' | 'pending'
  assignmentStatus: string; // 'pending', 'accepted_not_started', 'in_progress', 'completed', 'declined', 'conflict_declared'
  isArbiter: boolean; // true for evaluators beyond the first 2 (E3+), i.e. brought in due to score deviation
  finalScore: number | null; // Calculated weighted score
  conflictDetails: string | null;
  criteriaScores: {
    originality_risk: number | null;
    national_relevance: number | null;
    global_scalability: number | null;
    international_collaboration: number | null;
    technical_viability: number | null;
    results_quality: number | null;
  };
}

export interface ApplicationEvaluationsResponseDto {
  applicationId: string;
  applicationNumber: string;
  summary: EvaluationSummaryDto;
  evaluations: EvaluationListItemDto[];
  maximumCriteriaScoreDifference: number;
  maximumScoreDifference: number;
  minimumEvaluationsRequired: number;
}

export interface ApplicationWithAdminData
  extends Omit<SectionBasedApplication, 'status' | 'user'> {
  status: AdminApplicationStatus;
  reviewerAssignment?: ReviewerAssignment;
  statusHistory: StatusChange[];
  evaluatorAssignments?: EvaluatorAssignment[];
  evaluationSummary?: EvaluationSummaryDto;
  awardNotifiedAt?: string | null;
  user: {
    id: string;
    name: string;
    email: string;
    userProfile?: UserProfile;
  };
}

export interface ApplicationFilters {
  search?: string;
  status?: AdminApplicationStatus[];
  dateFrom?: string;
  dateTo?: string;
  country?: string;
  unidadAcademica?: string;
  evaluatorStatus?: string; // assigned, unassigned
  researcherType?: string; // consolidated, emerging
}

export interface AdminAction {
  type: 'assign_review' | 'mark_admissible' | 'mark_not_admissible';
  applicationId: string;
  notes?: string;
}

export type { PaginationData };

export interface PaginatedApplicationResponse {
  applications: ApplicationWithAdminData[];
  pagination: PaginationData;
}

export interface StatusApplicationsData {
  applications: ApplicationWithAdminData[];
  pagination: PaginationData;
}

// Status display configuration
export const StatusConfig: Record<
  string,
  { label: string; color: string; order: number }
> = {
  draft: { label: 'Borrador', color: 'gray', order: 0 },
  submitted: { label: 'Recibida', color: 'teal', order: 1 },
  under_admissibility_review: {
    label: 'En revisión de admisibilidad',
    color: 'yellow',
    order: 2,
  },
  not_admissible: { label: 'No admisible', color: 'red', order: 3 },
  admissible: { label: 'Admisible', color: 'green', order: 4 },
  under_evaluation: { label: 'En evaluación', color: 'purple', order: 5 },
  evaluated: { label: 'Evaluada', color: 'indigo', order: 6 },
  selected: { label: 'Adjudicada', color: 'green', order: 7 },
  not_selected: { label: 'No adjudicada', color: 'red', order: 8 },
};

// User Management Types
export interface AdminUserFilters {
  page?: number;
  limit?: number;
  role?: UserRole;
  affiliation?: string;
  unidadAcademica?: string;
  isActive?: boolean;
  isExternal?: boolean;
  canApply?: boolean;
  researcherType?: 'consolidated' | 'emerging';
  search?: string;
}

export interface AdminUsersResponse {
  users: User[];
  pagination: PaginationData;
}

export interface AdminUpdateUserDto {
  email?: string;
  name?: string;
  password?: string;
  role?: UserRole;
  isActive?: boolean;
  profile?: {
    run?: string;
    affiliation?: string;
    facultadUC?: string;
    unidadAcademica?: string;
    doctorateYear?: number;
    researcherType?: 'consolidated' | 'emerging';
    canApply?: boolean;
    platformMessage?: string;
  };
}

export interface SingleEvaluationResponseDto {
  id: string;
  assignmentId: string;
  criteriaEvaluations: Record<
    string,
    {
      score: number;
      comments: string;
      maxScore: number;
    }
  >;
  budgetComments: string | null;
  overallComments: string | null;
  conflictsDeclared: boolean;
  conflictDetails: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  scoreSummary: {
    finalScore: number;
    criteriaScores: Record<string, number>;
    isValid: boolean;
  };
  application: {
    id: string;
    applicationNumber: string;
    status: string;
    answers: any;
    user: {
      id: string;
      name: string;
      email: string;
    };
  };
  evaluator: {
    id: string;
    name: string;
    email: string;
    isExternal: boolean;
  };
  assignment: {
    id: string;
    assignedAt: string;
    status: string;
    conflictDeclared: boolean;
    conflictStatus: string | null;
  };
  finalScore: number | null;
}

// Application Alerts — 8 canonical types matching the spec
export enum AlertType {
  EVALUATION_SCORE_VARIANCE = 'evaluation_score_variance', // DESV_PUNTAJE
  DEADLINE_APPROACHING = 'deadline_approaching',           // PLAZO_CIERRE
  MANUAL = 'manual',                                       // Creada por administrador
  INVITATION_NO_RESPONSE = 'invitation_no_response',       // INVIT_SIN_RESP
  INVITATION_REJECTED = 'invitation_rejected',             // INVIT_RECHAZADA
  EVALUATION_DELAYED = 'evaluation_delayed',               // EVAL_ATRASADA
  EVALUATOR_NO_LOGIN = 'evaluator_no_login',               // EVAL_SIN_LOGIN
  EVALUATOR_OVERLOADED = 'evaluator_overloaded',           // SOBRECARGA
}

export enum AlertStatus {
  OPEN = 'open',
  RESOLVED = 'resolved',
  DISMISSED = 'dismissed',
}

export enum AlertPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

export interface ApplicationAlert {
  id: string;
  applicationId?: string | null;   // nullable for evaluator-only anchored alerts
  evaluatorId?: string | null;     // set for evaluator-anchored alerts
  assignmentId?: string | null;    // set for assignment-anchored alerts
  alertType: AlertType;
  title: string;
  description: string;
  priority: AlertPriority;
  status: AlertStatus;
  createdAt: Date;
  resolvedAt?: Date | null;
  resolvedBy?: string | null;
  resolutionNote?: string | null;
  dismissNote?: string | null;
  metadata?: any;
  // Enriched at query time
  application?: {
    id: string;
    applicationNumber: string;
    status: string;
    user: { id: string; name: string; email: string };
  } | null;
  evaluator?: { id: string; name: string; email: string } | null;
  resolvedByUser?: { id: string; name: string; email: string };
}

export interface AlertFilters {
  status?: AlertStatus;
  alertTypes?: AlertType[];
  applicationId?: string;
  evaluatorId?: string;
  priority?: AlertPriority;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface PaginatedAlertsResponse {
  data: ApplicationAlert[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// ── Evaluations Board (Tablero de Control de Evaluaciones) ──────────────────

// Escala de cobertura, de menos a más avanzada:
//   none      — no hay ningún evaluador activo
//   assigned  — hay evaluadores, pero a ninguno se le envió la invitación todavía
//   invited   — al menos una invitación salió y aún no hay aceptaciones
//   incomplete— un evaluador aceptó (bajo el mínimo del concurso)
//   complete  — se alcanzó el mínimo de aceptaciones
export type CoverageLabel = 'none' | 'assigned' | 'invited' | 'incomplete' | 'complete';
export type ProgressLabel = 'not_started' | 'in_progress' | 'overdue' | 'in_arbitration' | 'received';
export type ResultLabel = 'no_deviation' | 'requires_arbitration' | 'arbiter_invited' | 'arbiter_accepted' | 'arbitration_in_progress' | 'arbitration_resolved' | 'resolved';

export interface EvalBoardAssignment {
  evaluatorId: string;
  evaluatorName: string;
  status: 'pending' | 'accepted_not_started' | 'in_progress' | 'completed' | 'declined' | 'conflict_declared';
  daysSinceAssigned: number;
  finalScore: number | null;
  conflictDetails: string | null;
  declineReason: 'thematic' | 'conflict' | 'other' | null;
  declineNote: string | null;
}

export interface EvalBoardRow {
  applicationId: string;
  applicationNumber: string;
  title: string;
  irName: string;
  unidadAcademica: string;
  coverage: CoverageLabel;
  progress: ProgressLabel;
  daysOverdue: number;
  assignments: EvalBoardAssignment[];
  completedScores: number[];
  avgScore: number | null;
  maxDeviation: number | null;
  result: ResultLabel;
}

// ── Evaluations Board — Por Evaluador ───────────────────────────────────────

export interface EvalByEvaluatorAssignment {
  assignmentId: string;
  applicationId: string;
  applicationNumber: string;
  title: string;
  status: 'pending' | 'accepted_not_started' | 'in_progress' | 'completed' | 'declined' | 'conflict_declared';
  daysSinceAssigned: number;
  /** True cuando la asignación existe pero la invitación nunca salió. */
  notInvited: boolean;
  declineReason: 'thematic' | 'conflict' | 'other' | null;
  declineNote: string | null;
}

export interface EvalByEvaluatorRow {
  evaluatorId: string;
  evaluatorName: string;
  email: string;
  isExternal: boolean;
  /** Aún no confirma su cuenta: su invitación lleva el enlace para crear contraseña. */
  needsPasswordSetup: boolean;
  // DEV NOTE (2026-06-26): In production, lastActivityAt comes from a single user.lastActivityAt
  // field on the User model, updated by API middleware on every authenticated request. This covers
  // login, browsing, downloads — any meaningful action. Both this field and the EVALUATOR_NO_LOGIN
  // alert must read the same source for consistent behavior. The prototype approximates it as
  // max(lastLoginAt, lastEvalSubmittedAt); the real backend should use the middleware approach.
  lastActivityAt: string | null;
  daysSinceActivity: number; // -1 means never active; threshold: ALERT_CONFIG.EVALUATOR_LOGIN_DAYS
  maxDaysPending: number; // días de la asignación activa más antigua; -1 si no hay activas
  // Invitation counts
  totalInvitations: number;
  pendingCount: number;
  // Asignaciones creadas cuya invitación nunca se envió (status 'pending' sin
  // invitationSentAt). Es el subconjunto de pendingCount que el envío masivo
  // puede cubrir; el resto ya fue invitado y solo cabe recordarle.
  notInvitedCount: number;
  acceptedNotStartedCount: number;
  inProgressCount: number;
  completedCount: number;
  declinedCount: number;
  conflictCount: number;
  // Derived
  activeLoad: number; // pending + accepted_not_started + in_progress (occupies quota slot)
  acceptedCount: number; // accepted_not_started + in_progress + completed (historical: all who said yes)
  disponible: number; // Contest.maxAssignmentsPerEvaluator − (activeLoad + completedCount); cuota acumulativa — ≤0 = sin cupo
  // isOverloaded: (activeLoad + completedCount) >= ALERT_CONFIG.OVERLOAD_THRESHOLD (>= because at-capacity = overloaded).
  // In production, OVERLOAD_THRESHOLD must be Contest.maxAssignmentsPerEvaluator (per-contest config).
  // Completed evaluations free the quota slot. See alertCatalog.ts DEV NOTE.
  isOverloaded: boolean;
  isInactive: boolean;
  assignments: EvalByEvaluatorAssignment[];
  // Admin profile summary (score + notes) for inline display
  adminProfile: { score: number | null; latestNote: string | null };
}
